import { Errors } from '@livecontrol/scheduler/store';
import { useCallback, useState } from 'react';
import { useUploadFile } from '../use-upload-file';
import { useFinishAsset } from './use-finish-asset';
import { useGenerateUrl } from './use-generate-url';

interface Args {
    clientId: number;
    assetId: number;
    file: File;
    duration?: number;
}

const VIDEO_STATUS = {
    GENERATING_LINK: 'generating link',
    UPLOADING: 'uploading',
    FINISHING_ASSET: 'finishing asset',
    NONE: '',
    ERROR: 'error'
} as const;

type VideoStatus = typeof VIDEO_STATUS[keyof typeof VIDEO_STATUS];

export const useReplaceAsset = (): [
    (args: Args) => Promise<void>,
    {
        loading: boolean;
        status: VideoStatus;
        progress: number;
        data?: { url: string };
        error?: Error;
        abort?: (() => void);
    }
] => {
    const [data, setData] = useState<{ url: string }>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error>();
    const [status, setStatus] = useState<VideoStatus>(VIDEO_STATUS.NONE);

    const [generateUrl, generateResponse] = useGenerateUrl();
    const [uploadFile, uploadResponse] = useUploadFile();
    const [finishAsset, finishResponse] = useFinishAsset();

    return [
        useCallback(async ({ assetId, clientId, file, duration }: Args) => {
            setLoading(true);
            setData(undefined);
            setStatus(VIDEO_STATUS.NONE);

            try {
                setStatus(VIDEO_STATUS.GENERATING_LINK);

                const { destination_url, upload_url } = await generateUrl({ assetId, clientId }) ?? {};

                if (!destination_url || !upload_url) {
                    throw Errors.serverError();
                }

                setStatus(VIDEO_STATUS.UPLOADING);
                await uploadFile(upload_url, file, { headers: [['Content-Type', 'video/mp4'], ['content-disposition', 'attachment']], method: 'PUT' });
                setStatus(VIDEO_STATUS.FINISHING_ASSET);
                await finishAsset({ assetId, clientId, archiveVideoUrl: destination_url, duration });
                setData({ url: destination_url });
                setStatus(VIDEO_STATUS.NONE);
            } catch (error_: unknown) {
                setError(<Error>error_);
                setStatus(VIDEO_STATUS.ERROR);
            }

            setLoading(false);
        }, [generateUrl, uploadFile, finishAsset]),
        {
            error: error ?? generateResponse.error ?? uploadResponse.error ?? finishResponse.error,
            loading: loading || generateResponse.loading || uploadResponse.uploading || finishResponse.loading,
            progress: uploadResponse.progress.percentage,
            data,
            abort: uploadResponse.abort,
            status
        }
    ];
};
